import { useShallow } from 'zustand/shallow';
import { useGameStore, useCompanyStore, useProductStore, useMarketStore, useUIStore, useBuildingStore } from './index';
import type { GameDate } from '../types/events';

// Grouped selector for game state
export const useGameState = () => {
  return useGameStore(
    useShallow((state) => ({
      gameDate: state.gameDate,
      isPaused: state.isPaused,
      simulationSpeed: state.simulationSpeed,
      settings: state.settings,
    }))
  );
};

// Grouped selector for game actions
export const useGameActions = () => {
  return useGameStore(
    useShallow((state) => ({
      updateGameDate: state.updateGameDate,
      pauseGame: state.pauseGame,
      resumeGame: state.resumeGame,
      setSimulationSpeed: state.setSimulationSpeed,
      resetGame: state.resetGame,
    }))
  );
};

// Grouped selector for company financial data
export const useCompanyFinances = () => {
  return useCompanyStore(
    useShallow((state) => ({
      cash: state.finances.cash,
      revenue: state.finances.revenue,
      costs: state.finances.costs,
      profit: state.finances.profit,
      dailyStats: state.finances.dailyStats,
    }))
  );
};

// Grouped selector for company metrics
export const useCompanyMetrics = () => {
  return useCompanyStore(
    useShallow((state) => ({
      reputation: state.metrics.reputation,
      marketShare: state.metrics.marketShare,
      innovationIndex: state.metrics.innovationIndex,
      customerSatisfaction: state.metrics.customerSatisfaction,
      brandStrength: state.metrics.brandStrength,
    }))
  );
};

// Grouped selector for company actions
export const useCompanyActions = () => {
  return useCompanyStore(
    useShallow((state) => ({
      setCash: state.setCash,
      updateMetrics: state.updateMetrics,
      updateFinances: state.updateFinances,
      updateCompany: state.updateCompany,
      setResearchPoints: state.setResearchPoints,
      updateResearch: state.updateResearch,
      setEmployees: state.setEmployees,
      addEmployee: state.addEmployee,
      removeEmployee: state.removeEmployee,
      updateEmployee: state.updateEmployee,
      resetCompany: state.resetCompany,
      updateDailyStats: state.updateDailyStats,
    }))
  );
};

// Grouped selector for company data
export const useCompanyData = () => {
  return useCompanyStore(
    useShallow((state) => ({
      company: state.company,
      companyDetails: state.companyDetails,
      research: state.research,
      employees: state.employees,
      hiringProcesses: state.hiringProcesses,
    }))
  );
};

// Grouped selector for product data
export const useProductData = () => {
  return useProductStore(
    useShallow((state) => ({
      products: state.products,
      pipeline: state.pipeline,
      developmentPhases: state.developmentPhases,
    }))
  );
};

// Grouped selector for product actions
export const useProductActions = () => {
  return useProductStore(
    useShallow((state) => ({
      setProducts: state.setProducts,
      createProduct: state.createProduct,
      updateProduct: state.updateProduct,
      discontinueProduct: state.discontinueProduct,
      moveProductToNextPhase: state.moveProductToNextPhase,
      startDevelopmentPhase: state.startDevelopmentPhase,
      updateDevelopmentPhase: state.updateDevelopmentPhase,
      completeDevelopmentPhase: state.completeDevelopmentPhase,
      updateMultipleProducts: state.updateMultipleProducts,
    }))
  );
};

// Grouped selector for market data
export const useMarketData = () => {
  return useMarketStore(
    useShallow((state) => ({
      totalMarketSize: state.totalMarketSize,
      marketGrowthRate: state.marketGrowthRate,
      economicPhase: state.economicPhase,
      competitorActions: state.competitorActions,
      technologicalDisruptions: state.technologicalDisruptions,
      marketEvents: state.marketEvents,
      playerActions: state.playerActions,
    }))
  );
};

// Grouped selector for building data and actions
export const useBuildingData = () => {
  return useBuildingStore(
    useShallow((state) => ({
      buildings: state.buildings,
      isInitialized: state.isInitialized,
      initializeWithStartingBuilding: state.initializeWithStartingBuilding,
      updateConstructionStatus: state.updateConstructionStatus,
      getCapacity: state.getCapacity,
      getMaintenanceCosts: state.getMaintenanceCosts,
      getAllBuildingStats: state.getAllBuildingStats,
      getCapacityBreakdown: state.getCapacityBreakdown,
    }))
  );
};

// Grouped selector for UI state
export const useUIState = () => {
  return useUIStore(
    useShallow((state) => ({
      activeModals: state.activeModals,
      openModal: state.openModal,
      closeModal: state.closeModal,
      toast: state.toast,
      showToast: state.showToast,
    }))
  );
};

// Combined selector for game simulation data
export const useGameSimulationData = () => {
  const gameState = useGameState();
  const finances = useCompanyFinances();
  const buildingData = useBuildingData();
  const { employees } = useCompanyData();
  const { products } = useProductData();
  
  return {
    gameDate: gameState.gameDate,
    isPaused: gameState.isPaused,
    simulationSpeed: gameState.simulationSpeed,
    cash: finances.cash,
    employees,
    buildings: buildingData.buildings,
    activeProducts: products.filter(p => p.status === 'production'),
  };
};

// Create a batched update action for game tick
export const useGameTickUpdate = () => {
  const { setCash, updateFinances, updateDailyStats, updateMetrics } = useCompanyActions();
  const { updateGameDate } = useGameActions();

  return (updates: {
    newDate: GameDate;
    cash: number;
    dailyRevenue: number;
    dailyCosts: number;
    salesBreakdowns: Array<{ productId: string; unitsSold: number; revenue: number }>;
    reputation?: number;
  }) => {
    // Batch all updates together
    updateGameDate(updates.newDate);
    setCash(updates.cash);
    updateFinances({
      revenue: updates.dailyRevenue,
      costs: updates.dailyCosts,
      profit: updates.dailyRevenue - updates.dailyCosts,
    });
    updateDailyStats({
      revenue: updates.dailyRevenue,
      costs: updates.dailyCosts,
      profit: updates.dailyRevenue - updates.dailyCosts,
      salesBreakdowns: updates.salesBreakdowns,
    });
    // Update reputation if provided
    if (updates.reputation !== undefined) {
      updateMetrics({ reputation: updates.reputation });
    }
  };
};